import { useEffect } from 'react';
import { useWebSocket } from 'ahooks';
import { ReadyState } from 'ahooks/lib/useWebSocket';
import { useMounted } from '@mantine/hooks';
import { useAuthStore } from '@/src/modules/auth/stores/use-auth-store';
import {
  createChatwootSubscribeMessage,
  mapChatwootMessageCreatedMessageToChatMessageModel,
} from '@/src/modules/chat/data/mappers';
import {
  SChatwootStandardMessage,
  TChatwootStandardMessage,
} from '@/src/modules/chat/data/schemas/chatwoot/messages/base';
import {
  SChatwootMessageCreatedMessage,
  TChatwootMessageCreatedMessage,
} from '@/src/modules/chat/data/schemas/chatwoot/messages/message-created';
import { useUpdateComplaintUserLastSeen } from '@/src/modules/chat/hooks/use-update-complaint-user-last-seen';
import { useUpdateMarketplaceCustomerLastSeen } from '@/src/modules/chat/hooks/use-update-marketplace-customer-last-seen';
import { useUpdateSelfMedicationUserLastSeen } from '@/src/modules/chat/hooks/use-update-self-medication-user-last-seen';
import { safeParseZodObject } from '@/src/shared/utils/parser';
import { useMarketplaceCustomerChatContext } from '../_contexts';

interface IUseMarketplaceSellerWebsocketParams {
  setCurrentLastScrollHeight: () => void;
}

const IGNORED_MESSAGE_TYPES = ['ping', 'welcome'];

export function useMarketplaceCustomerWebsocket({
  setCurrentLastScrollHeight,
}: IUseMarketplaceSellerWebsocketParams) {
  const userId = useAuthStore((state) => state.user?.props.id);
  const {
    selectedConversationId,
    addCurrentConversationMessages,
    selectedConversation,
    currentConversationContact,
    selfMedicationConversation,
    complaintConversation,
  } = useMarketplaceCustomerChatContext();
  const isMounted = useMounted();

  const { trigger: updateCustomerLastSeen } =
    useUpdateMarketplaceCustomerLastSeen({
      conversationId: selectedConversationId,
      userId: userId || null,
    });
  const { trigger: updateSelfMedicationLastSeen } =
    useUpdateSelfMedicationUserLastSeen({
      userId: userId || null,
    });
  const { trigger: updateComplaintLastSeen } = useUpdateComplaintUserLastSeen({
    userId: userId || null,
  });

  const {
    readyState,
    connect: connectToChatwootWs,
    disconnect: disconnectFromChatwootWs,
    sendMessage: sendMessageToChatwootWs,
  } = useWebSocket(process.env.NEXT_PUBLIC_CHATWOOT_WEBSOCKET_URL!, {
    onMessage: (event) => {
      const data = JSON.parse(event.data);
      if (IGNORED_MESSAGE_TYPES.includes(data.type)) {
        return;
      }

      const baseMessage = safeParseZodObject<TChatwootStandardMessage>(
        SChatwootStandardMessage,
        data
      );

      if (baseMessage.message?.event === 'message.created') {
        setCurrentLastScrollHeight();
        handleOnMessageCreatedEvent(baseMessage);
      }
    },
    reconnectLimit: 5,
    manual: true,
  });

  const selfMedicationConversationIsSelected =
    selectedConversation?.props.id === selfMedicationConversation?.props.id;
  const complaintConversationIsSelected =
    selectedConversation?.props.id === complaintConversation?.props.id;

  const handleOnMessageCreatedEvent = (
    baseMessage: TChatwootStandardMessage
  ) => {
    const messageCreatedMessage =
      safeParseZodObject<TChatwootMessageCreatedMessage>(
        SChatwootMessageCreatedMessage,
        baseMessage.message?.data
      );
    if (messageCreatedMessage.conversation_id !== selectedConversationId) {
      return;
    }

    const message = mapChatwootMessageCreatedMessageToChatMessageModel(
      messageCreatedMessage,
      currentConversationContact!.props.id
    );
    addCurrentConversationMessages(message);

    if (selfMedicationConversationIsSelected) {
      updateSelfMedicationLastSeen();
    } else if (complaintConversationIsSelected) {
      updateComplaintLastSeen();
    } else {
      updateCustomerLastSeen();
    }

    selectedConversation?.setLastMessage(message);
  };

  // Send subscribe message to websocket when connection is established
  // to receive, moving this action to the onOpen callback of useWebSocket will
  // cause problem because of sendMessage function reference is not stable, it
  // will cause the component to be re-rendered and the websocket connection
  // will be closed and reconnected again and eventually the sendMessage
  // function called when the connection is not ready yet
  useEffect(() => {
    if (!isMounted) {
      return;
    }

    if (readyState === ReadyState.Open) {
      const message = createChatwootSubscribeMessage({
        pubsubToken: currentConversationContact!.props.pubsubToken,
      });
      sendMessageToChatwootWs(message);
    }
  }, [readyState]);

  // Conditionally connect to websocket when contact is available,
  // and disconnect when contact is not available anymore (e.g. user logout)
  useEffect(() => {
    if (!isMounted) {
      return;
    }

    if (currentConversationContact) {
      connectToChatwootWs();
      return;
    }

    disconnectFromChatwootWs();
  }, [isMounted, currentConversationContact]);
}
